import { countCharacters, platformsOverLimit } from '../lib/platforms';

/**
 * Live caption-length readout for a field whose text becomes, or shapes, a
 * caption.
 *
 * Pure derivation from the controlled value: no state, no effect, no API call.
 * The numbers come from the local mirror of the backend's platform limits, so
 * this agrees with the deterministic copy guardrail that runs later.
 *
 * It is deliberately quiet. Listing every platform as passing would be noise —
 * Facebook's 63,206 never bites — so the common case is a single calm line and
 * only the limits actually passed get named, in stamp-red.
 */
export function CharMeter({ text }: { text: string }) {
  // Nothing typed yet is not a measurement; a blank field says nothing.
  if (text.trim().length === 0) return null;

  const count = countCharacters(text);
  const overflows = platformsOverLimit(count);

  return (
    <p className="char-meter">
      <span className="char-meter__count">{count}</span>
      <span className="char-meter__unit">characters</span>
      {/* Only the verdict is announced: the count changes on every keystroke,
          the fits/over state changes only when a limit is crossed. */}
      <span className="char-meter__verdict" role="status" aria-live="polite">
        {overflows.length === 0 ? (
          <span className="char-meter__fits">Fits every platform</span>
        ) : (
          <>
            <span className="char-meter__over-label">Over</span>
            {overflows.map((overflow) => (
              <span className="char-meter__over" key={overflow.platform}>
                {overflow.label} <span className="char-meter__limit">{overflow.limit}</span>
              </span>
            ))}
          </>
        )}
      </span>
    </p>
  );
}
